import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { UiSelect } from '../components/UiSelect';
import toast from 'react-hot-toast';
import { Ticket, Plus, Edit, Trash2, BadgePercent, Gift, Save } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { SkeletonTable, EmptyState, ButtonLoader } from '../components/LoadingStates';
import { couponService, Coupon, CouponType, CreateCouponRequest } from '../services/coupon';
import { loyaltyService, LoyaltyConfig } from '../services/loyalty';
import { Modal, Field, ModalActions, Th, inputCls } from './inventory/StockItemsTab';
import { formatCurrency, formatDate } from './inventory/inventoryUtils';

type Tab = 'coupons' | 'loyalty';

export const Promotions: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;
  const [tab, setTab] = useState<Tab>('coupons');

  if (!org) {
    return <div className="p-6"><EmptyState icon={<Ticket className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." /></div>;
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3.5 mb-6">
        <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
          <BadgePercent className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Promotions &amp; Loyalty</h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Run discount coupons and a points-based loyalty programme</p>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b mb-6" style={{ borderColor: currentTheme.colors.border.primary }}>
        {([['coupons', 'Coupons', Ticket], ['loyalty', 'Loyalty', Gift]] as [Tab, string, any][]).map(([key, label, Icon]) => {
          const active = tab === key;
          return (
            <button key={key} onClick={() => setTab(key)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px"
              style={{ borderColor: active ? currentTheme.colors.primary[600] : 'transparent', color: active ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary }}>
              <Icon className="h-4 w-4" />{label}
            </button>
          );
        })}
      </div>

      {tab === 'coupons' ? <CouponsTab org={org} restaurantId={restaurantId} /> : <LoyaltyTab org={org} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Coupons
// ---------------------------------------------------------------------------
interface CouponFormData {
  code: string;
  description?: string;
  type: CouponType;
  value: number;
  minOrderAmount?: number;
  maxDiscount?: number;
  validFrom?: string;
  validTo?: string;
  usageLimit?: number;
}

const CouponsTab: React.FC<{ org: string; restaurantId?: string }> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; id: string; label: string }>({ open: false, id: '', label: '' });
  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm<CouponFormData>();
  const couponType = watch('type');

  const { data: coupons, isLoading } = useQuery(['coupons', org, restaurantId], () => couponService.list(org, restaurantId));

  const createMutation = useMutation(couponService.create, {
    onSuccess: () => { queryClient.invalidateQueries(['coupons']); close(); toast.success('Coupon created'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to create coupon'); },
  });
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateCouponRequest> }) => couponService.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['coupons']); close(); toast.success('Coupon updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update coupon'); },
    },
  );
  const toggleMutation = useMutation(couponService.toggleActive, {
    onSuccess: () => { queryClient.invalidateQueries(['coupons']); toast.success('Status updated'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });
  const deleteMutation = useMutation(couponService.remove, {
    onSuccess: () => { queryClient.invalidateQueries(['coupons']); toast.success('Coupon deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  function close() { setIsFormOpen(false); setEditing(null); reset(); }
  function openCreate() { setEditing(null); reset({ type: 'PERCENT', value: 10 }); setIsFormOpen(true); }
  function openEdit(c: Coupon) {
    setEditing(c);
    reset({ code: c.code, description: c.description, type: c.type, value: c.value, minOrderAmount: c.minOrderAmount, maxDiscount: c.maxDiscount, validFrom: c.validFrom, validTo: c.validTo, usageLimit: c.usageLimit });
    setIsFormOpen(true);
  }

  const onSubmit = (data: CouponFormData) => {
    const payload: CreateCouponRequest = {
      ...data,
      value: Number(data.value),
      minOrderAmount: data.minOrderAmount ? Number(data.minOrderAmount) : undefined,
      maxDiscount: data.maxDiscount ? Number(data.maxDiscount) : undefined,
      usageLimit: data.usageLimit ? Number(data.usageLimit) : undefined,
      organizationCode: org,
      ...(restaurantId && { restaurantId }),
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openCreate} className="inline-flex items-center btn-primary">
          <Plus className="h-5 w-5 mr-2" /> New Coupon
        </button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : !coupons || coupons.length === 0 ? (
        <EmptyState icon={<Ticket className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No coupons"
          description="Create discount codes customers can use at checkout."
          action={<button onClick={openCreate} className="inline-flex items-center btn-primary"><Plus className="h-5 w-5 mr-2" /> New Coupon</button>} />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Code</Th><Th>Discount</Th><Th>Min Order</Th><Th>Validity</Th><Th>Used</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {coupons.map(c => (
                <tr key={c.id} className={clsx(!c.active && 'opacity-60')}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-sm" style={{ color: currentTheme.colors.text.primary }}>{c.code}</div>
                    {c.description && <div className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>{c.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>
                    {c.type === 'PERCENT' ? `${c.value}%${c.maxDiscount ? ` (max ${formatCurrency(c.maxDiscount)})` : ''}` : formatCurrency(c.value)}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{c.minOrderAmount ? formatCurrency(c.minOrderAmount) : '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                    {c.validFrom || c.validTo ? `${c.validFrom ? formatDate(c.validFrom) : '—'} → ${c.validTo ? formatDate(c.validTo) : '—'}` : 'Always'}
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{c.usedCount ?? 0}{c.usageLimit ? ` / ${c.usageLimit}` : ''}</td>
                  <td className="px-4 py-3"><span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', c.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>{c.active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleMutation.mutate(c.id)} className="px-2 py-1 rounded-lg text-xs font-medium" style={{ backgroundColor: c.active ? '#fef2f2' : '#f0fdf4', color: c.active ? '#dc2626' : '#16a34a' }}>{c.active ? 'Off' : 'On'}</button>
                      <button onClick={() => openEdit(c)} className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }}><Edit className="h-4 w-4" /></button>
                      <button onClick={() => setConfirm({ open: true, id: c.id, label: c.code })} className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <Modal title={editing ? 'Edit Coupon' : 'New Coupon'} onClose={close} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Code" error={errors.code?.message} required>
                <input {...register('code', { required: 'Code is required' })} className={clsx(inputCls, 'uppercase')} placeholder="e.g., WELCOME10" disabled={!!editing} />
              </Field>
              <Field label="Type" required>
                <Controller name="type" control={control} rules={{ required: true }} render={({ field }) => (
                  <UiSelect value={field.value || ''} onChange={field.onChange}
                    options={[{ value: 'PERCENT', label: 'Percentage (%)' }, { value: 'FLAT', label: 'Flat amount (₹)' }]} />
                )} />
              </Field>
              <Field label={couponType === 'FLAT' ? 'Discount Amount (₹)' : 'Discount Percent (%)'} error={errors.value?.message} required>
                <input type="number" step="any" min="0" {...register('value', { required: 'Value is required', min: { value: 0, message: '≥ 0' } })} className={inputCls} />
              </Field>
              {couponType === 'PERCENT' && (
                <Field label="Max Discount (₹)"><input type="number" step="any" min="0" {...register('maxDiscount')} className={inputCls} placeholder="optional cap" /></Field>
              )}
              <Field label="Min Order Amount (₹)"><input type="number" step="any" min="0" {...register('minOrderAmount')} className={inputCls} placeholder="optional" /></Field>
              <Field label="Usage Limit"><input type="number" min="0" {...register('usageLimit')} className={inputCls} placeholder="unlimited" /></Field>
              <Field label="Valid From"><input type="date" {...register('validFrom')} className={inputCls} /></Field>
              <Field label="Valid To"><input type="date" {...register('validTo')} className={inputCls} /></Field>
            </div>
            <Field label="Description"><input {...register('description')} className={inputCls} placeholder="e.g., Welcome offer for new customers" /></Field>
            <ModalActions onCancel={close} loading={createMutation.isLoading || updateMutation.isLoading}
              submitLabel={editing ? 'Update' : 'Create'} loadingLabel={editing ? 'Updating...' : 'Creating...'} />
          </form>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={() => { deleteMutation.mutate(confirm.id); setConfirm(c => ({ ...c, open: false })); }}
        title="Delete Coupon" message={`Delete coupon "${confirm.label}"?`} confirmText="Delete" type="danger" isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loyalty config
// ---------------------------------------------------------------------------
const LoyaltyTab: React.FC<{ org: string }> = ({ org }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<LoyaltyConfig | null>(null);

  const { isLoading } = useQuery(['loyalty-config', org], () => loyaltyService.getConfig(org), {
    onSuccess: (data) => setForm(data),
  });

  const saveMutation = useMutation((cfg: LoyaltyConfig) => loyaltyService.saveConfig(org, cfg), {
    onSuccess: () => { queryClient.invalidateQueries(['loyalty-config']); toast.success('Loyalty settings saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to save'); },
  });

  if (isLoading || !form) return <SkeletonTable rows={4} columns={2} />;

  const set = (patch: Partial<LoyaltyConfig>) => setForm(f => f ? { ...f, ...patch } : f);

  return (
    <div className="max-w-xl">
      <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-5 space-y-5" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold" style={{ color: currentTheme.colors.text.primary }}>Enable Loyalty Programme</p>
            <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>Customers earn points on spend and redeem them for discounts</p>
          </div>
          <button onClick={() => set({ enabled: !form.enabled })}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            style={{ backgroundColor: form.enabled ? currentTheme.colors.primary[600] : '#d1d5db' }}>
            <span className={clsx('inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform', form.enabled ? 'translate-x-6' : 'translate-x-1')} />
          </button>
        </div>

        <Field label="Points earned per ₹100 spent">
          <input type="number" step="any" min="0" value={form.earnPointsPer100} onChange={e => set({ earnPointsPer100: Number(e.target.value) })} className={inputCls} />
        </Field>
        <Field label="₹ value per point (on redemption)">
          <input type="number" step="any" min="0" value={form.redeemValuePerPoint} onChange={e => set({ redeemValuePerPoint: Number(e.target.value) })} className={inputCls} />
        </Field>
        <Field label="Minimum points before redeeming">
          <input type="number" min="0" value={form.minRedeemPoints} onChange={e => set({ minRedeemPoints: Number(e.target.value) })} className={inputCls} />
        </Field>

        <div className="flex justify-end">
          <button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isLoading}
            className="inline-flex items-center gap-2 px-5 py-2 text-white rounded-lg btn-primary disabled:opacity-50" style={{ backgroundColor: currentTheme.colors.primary[600] }}>
            {saveMutation.isLoading ? <ButtonLoader text="Saving..." /> : <><Save className="h-4 w-4" /> Save</>}
          </button>
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: currentTheme.colors.text.secondary }}>
        Points are tracked per customer. Adjust a customer's points from their profile, or wire automatic earning into order settlement.
      </p>
    </div>
  );
};
